import { FamilyMember } from '@/types/familyMember';
import { FamilyMemberEmail, dbRowToFamilyMemberEmail } from '@/types/familyMemberEmail';

/**
 * Utility for mapping between Supabase Database rows and Frontend FamilyMember objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// SEC-M7 phase 10: explicit read allowlist for `family_members` rows. Keep in
// sync with the columns consumed by `dbRowToMember` below. Future internal /
// owner-only columns added to `family_members` (e.g. private notes, billing
// flags, audit fields) won't auto-flow to teammates until added here.
export const FAMILY_MEMBER_COLUMNS =
  'id, name, current_streak, longest_streak, last_active_date, email, user_id, created_at, team_id, sort_order, is_visible, point_balance, avatar_type, avatar_value';

export const dbRowToMember = (row: any, emails: FamilyMemberEmail[] = []): FamilyMember => ({
  id: row.id,
  name: row.name,
  currentStreak: row.current_streak ?? 0,
  longestStreak: row.longest_streak ?? 0,
  lastActiveDate: row.last_active_date ? new Date(row.last_active_date) : null,
  email: row.email || null,
  userId: row.user_id || null,
  emails: emails.filter(e => e.familyMemberId === row.id),
  createdAt: new Date(row.created_at),
  teamId: row.team_id || null,
  sortOrder: row.sort_order ?? 0,
  isVisible: row.is_visible ?? true,
  pointBalance: row.point_balance ?? 0,
  avatarType: (row.avatar_type as FamilyMember['avatarType']) ?? null,
  avatarValue: row.avatar_value ?? null,
});

export const mapMemberToDb = (updates: Partial<FamilyMember>): any => {
  const dbUpdates: any = {};

  if ('name' in updates && updates.name !== undefined) dbUpdates.name = updates.name.trim();
  if ('email' in updates) dbUpdates.email = updates.email?.trim() || null;
  if ('userId' in updates) dbUpdates.user_id = updates.userId;
  if ('teamId' in updates) dbUpdates.team_id = updates.teamId;
  if ('sortOrder' in updates) dbUpdates.sort_order = updates.sortOrder;
  if ('isVisible' in updates) dbUpdates.is_visible = updates.isVisible;
  if ('pointBalance' in updates) dbUpdates.point_balance = updates.pointBalance;
  if ('avatarType' in updates) dbUpdates.avatar_type = updates.avatarType ?? null;
  if ('avatarValue' in updates) dbUpdates.avatar_value = updates.avatarValue ?? null;

  return dbUpdates;
};

export const deserializeMemberFromCache = (row: any): FamilyMember => {
  const createdAt = row.createdAt ?? row.created_at;
  return {
    id: row.id,
    name: row.name,
    currentStreak: row.currentStreak ?? row.current_streak ?? 0,
    longestStreak: row.longestStreak ?? row.longest_streak ?? 0,
    lastActiveDate: row.lastActiveDate ? new Date(row.lastActiveDate) : (row.last_active_date ? new Date(row.last_active_date) : null),
    email: row.email ?? null,
    userId: row.userId ?? row.user_id ?? null,
    emails: (row.emails || []).map((e: any) => ({
      ...e,
      createdAt: e.createdAt ? new Date(e.createdAt) : new Date(),
    })),
    createdAt: createdAt ? new Date(createdAt) : new Date(),
    teamId: row.teamId ?? row.team_id ?? null,
    sortOrder: row.sortOrder ?? row.sort_order ?? 0,
    isVisible: row.isVisible ?? row.is_visible ?? true,
    pointBalance: row.pointBalance ?? row.point_balance ?? 0,
    avatarType: (row.avatarType ?? row.avatar_type ?? null) as FamilyMember['avatarType'],
    avatarValue: row.avatarValue ?? row.avatar_value ?? null,
  };
};
